import _ from "lodash";
import { entities } from "../components/common/constants";

// for calculating left position of each pinned column
export const getPinnedColumnLeftPositions = (pinnedColumns, pinnedColumnsRef, defaultLeft = 54) => {
    let columnLeftValues = {};
    pinnedColumns.forEach(columnKey => {
        const columnIndex = pinnedColumns.indexOf(columnKey);
        const leftColumns = pinnedColumns.slice(0, columnIndex);
        let leftColumnsWidth = 0;
        leftColumns.forEach(id => {
            const columnElement = pinnedColumnsRef?.[id];
            if (columnElement) {
                leftColumnsWidth += columnElement.offsetWidth;
            }
        });
        columnLeftValues[columnKey] = `${leftColumnsWidth + defaultLeft}px`;
    });
    return columnLeftValues;
};

/**
 * Inserts fields into an array before or after a specified reference field.
 * @param {Array} array - The array to insert fields into.
 * @param {Array} fields - The fields to insert.
 * @param {string} referenceField - The reference field to insert fields before or after.
 * @param {string} position - The position to insert fields ('before' or 'after'). Defaults to 'before'.
 */
export const insertFields = (array, fields, referenceField, position = "before") => {
    const index = array.findIndex(item => item === referenceField);
    if (index !== -1) {
        array.splice(position === "after" ? index + 1 : index, 0, ...fields);
    }
};

/**
 * Removes specified fields from an array.
 * @param {Array} array - The array to remove fields from.
 * @param {Array} fields - The fields to remove.
 */
export const removeFields = (array, fields) => {
    fields.forEach(field => {
        const index = array.indexOf(field);
        if (index !== -1) {
            array.splice(index, 1);
        }
    });
};

/**
 * @param {Object} params
 * @param {Object} params.defaultConfig           – your base config object
 * @param {string[]} params.defaultKeys           – your base key order
 * @param {Object|null} params.tableSettings      – backend table_settings or null
 * @param {boolean} params.isStaticCR             – for CR vs. Recommendation static columns
 * @param {string} params.capitalRequestLabel     – project display label for capital requests
 * @param {boolean} params.dynamicCR              – for filtering dynamic_fields.visible_type
 * @param {Object} params.recommendationCustomFields – { bandName: [ fieldObj, … ], … }
 * @param {Object[]} params.priorityElements      – priority_elements array from API
 * @param {boolean} params.displayUnit            – project.display_unit flag
 * @param {boolean} params.showEnergyBand         – project.show_energy_band flag
 * @param {Object[]} params.energyFields          – array of energy band definitions
 * @param {boolean} params.showWaterBand          – project.show_water_band flag
 * @param {Object[]} params.waterFields           – array of water band definitions
 *
 * @returns {{config:Object, keys:string[], paneViewEnabled:boolean}}
 */
export function computeTableConfig({
    defaultConfig = {},
    defaultKeys = [],
    tableSettings = null,
    isStaticCR = false,
    capitalRequestLabel = "Capital Request",
    dynamicCR = false,
    recommendationCustomFields = {},
    priorityElements = [],
    displayUnit = false,
    showEnergyBand = false,
    energyFields = [],
    showWaterBand = false,
    waterFields = [],
    entity = ""
}) {
    // 1) start from defaults or saved
    let tempConfig = { ...defaultConfig };
    let tempKeys = [];
    let paneViewEnabled = false;

    if (tableSettings) {
        const { config: backendConfig = {}, keys: savedKeys = [], paneViewEnabled: savedPane = false } = tableSettings;

        paneViewEnabled = savedPane;
        tempConfig = { ...defaultConfig };
        if (Array.isArray(savedKeys)) {
            const savedKeysSet = new Set(savedKeys);
            const newKeys = defaultKeys.filter(key => !savedKeysSet.has(key));
            tempKeys = [...savedKeys, ...newKeys];
        } else {
            tempKeys = [...defaultKeys];
        }

        Object.entries(backendConfig).forEach(([k, override]) => {
            const overrideVisible =
                override.isVisible === false || override.isVisible === "false" || override.isVisible === 0 || override.isVisible === "0"
                    ? false
                    : override.isVisible === true || override.isVisible === "true" || override.isVisible === 1 || override.isVisible === "1"
                    ? true
                    : undefined;
            tempConfig[k] = {
                ...tempConfig[k],
                style: override.style ?? tempConfig[k]?.style,
                isVisible: overrideVisible ?? tempConfig[k]?.isVisible,
                pinned: override.pinned ?? tempConfig[k]?.pinned
            };
        });
    } else {
        tempConfig = { ...defaultConfig };
        tempKeys = [...defaultKeys];
    }

    if (entity === entities.RECOMMENDATIONS) {
        const staticKeys = [...defaultKeys];
        const unitFields = ["unit", "cost_per_unit", "quantity", "options_cost", "total_opinions_of_cost", "occurrence"];
        const CR_ONLY_FIELDS = ["requestor_name", "requested_date", "cr_status", "cr_status_notes", "has_recommendations"];
        const RECO_STATUS_FIELDS = ["status", "status_notes", "surveyor", "inspection_date"];
        const RECO_FIELDS_TO_EXCLUDE = ["status", "surveyor", "inspection_date"];

        const baseStaticKeys = displayUnit ? staticKeys.concat(unitFields) : staticKeys;
        const allowedStaticKeys = isStaticCR
            ? baseStaticKeys.filter(k => !RECO_FIELDS_TO_EXCLUDE.includes(k)).concat(CR_ONLY_FIELDS.filter(f => !baseStaticKeys.includes(f)))
            : baseStaticKeys.filter(k => !CR_ONLY_FIELDS.includes(k));

        const dynamicNames = Object.values(recommendationCustomFields).flatMap(arr =>
            arr
                .filter(f => {
                    const vt = f.rules?.visible_type ?? "both";
                    return vt === "both" || (dynamicCR ? vt === "cr_only" : vt === "recommendation_only");
                })
                .map(f => f.field_name)
                .filter(Boolean)
        );

        Object.keys(tempConfig).forEach(k => {
            if (!allowedStaticKeys.includes(k) && !dynamicNames.includes(k)) {
                delete tempConfig[k];
            }
        });

        tempKeys = tempKeys.filter(k => allowedStaticKeys.includes(k) || dynamicNames.includes(k));

        if (isStaticCR) {
            tempConfig.description = {
                ...(tempConfig.description || {}),
                label: capitalRequestLabel
            };
            tempConfig.notes = {
                ...(tempConfig.notes || {}),
                label: `${capitalRequestLabel} Narrative`
            };
            if (tempConfig.cr_status) {
                tempConfig.cr_status = {
                    ...(tempConfig.cr_status || {}),
                    label: `${capitalRequestLabel} Status`
                };
            }
            if (tempConfig.cr_status_notes) {
                tempConfig.cr_status_notes = {
                    ...(tempConfig.cr_status_notes || {}),
                    label: `${capitalRequestLabel} Status Notes`
                };
            }
            removeFields(tempKeys, ["has_cr"]);
            delete tempConfig.has_cr;
            const crAnchor = tempKeys.includes("status_notes") ? "status_notes" : "source";
            const crFieldsToInsert = CR_ONLY_FIELDS.filter(f => !tempKeys.includes(f));
            if (crFieldsToInsert.length) {
                insertFields(
                    tempKeys,
                    crFieldsToInsert?.filter(f => f !== "cr_status"),
                    crAnchor,
                    "after"
                );
                insertFields(tempKeys, ["cr_status"], "code", "after");
            }
            removeFields(tempKeys, RECO_STATUS_FIELDS);
            RECO_STATUS_FIELDS.forEach(k => delete tempConfig[k]);
        } else {
            tempConfig.description = {
                ...(tempConfig.description || {}),
                label: "Recommendation"
            };
            tempConfig.notes = {
                ...(tempConfig.notes || {}),
                label: "Recommendation Narrative"
            };
            insertFields(tempKeys, ["status", "status_notes"], "cr_status", "before");
            insertFields(tempKeys, ["surveyor", "inspection_date"], "requestor_name", "before");
            removeFields(tempKeys, CR_ONLY_FIELDS);
            CR_ONLY_FIELDS.forEach(k => delete tempConfig[k]);
        }

        if (displayUnit) {
            const toInsert = unitFields.filter(f => !tempKeys.includes(f));
            if (toInsert.length) {
                const idx = tempKeys.length > 1 ? tempKeys.length - 2 : 0;
                tempKeys.splice(idx, 0, ...toInsert);
            }
        } else {
            removeFields(tempKeys, unitFields);
        }

        // 5) priority elements
        if (priorityElements.length) {
            const validSet = new Set();
            priorityElements.forEach((item, i) => {
                const key = `priority_element${i + 1}`;
                const idx = tempKeys.length > 1 ? tempKeys.length - 2 : 0;
                if (!tempKeys.includes(key)) tempKeys.splice(idx, 0, key);
                tempConfig[key] = {
                    ...(tempConfig[key] || {}),
                    isVisible: tempConfig[key]?.isVisible ?? true,
                    label: item.display_name || `Priority Element ${i + 1}`,
                    class: "width-230px",
                    searchKey: `priority_elements.${item.id}`,
                    type: "text",
                    hasWildCardSearch: true,
                    hasCommonSearch: false,
                    getListTable: "priority_element",
                    priorityIndex: i + 1,
                    commonSearchKey: "priority_elements",
                    commonSearchObjectKey: `${item.id}`,
                    pinned: tempConfig[key]?.pinned,
                    style: tempConfig[key]?.style,
                    isPriorityElement: true,
                    keyId: item.id,
                    hasLineEdit: false,
                    isDropdownLineEdit: true
                };
                validSet.add(key);
            });
            tempKeys = tempKeys.filter(k => (!k.startsWith("priority_element") ? true : validSet.has(k)));
            Object.keys(tempConfig).forEach(k => {
                if (k.startsWith("priority_element") && !validSet.has(k)) delete tempConfig[k];
            });
        } else {
            tempKeys = tempKeys.filter(k => !k.startsWith("priority_element"));
            Object.keys(tempConfig).forEach(k => {
                if (k.startsWith("priority_element")) delete tempConfig[k];
            });
        }

        // 6) energy band — now gated by showEnergyBand
        if (showEnergyBand && energyFields.length) {
            const idx = tempKeys.length > 1 ? tempKeys.length - 2 : 0;
            if (!tempKeys.includes("energy_band_show")) tempKeys.splice(idx, 0, "energy_band_show");

            energyFields.forEach((item, i) => {
                const key = `energy_band_${i}`;
                if (!tempKeys.includes(key)) tempKeys.splice(idx, 0, key);
                tempConfig[key] = {
                    ...(tempConfig[key] || {}),
                    isVisible: tempConfig[key]?.isVisible ?? true,
                    label: item.label,
                    class: "width-230px",
                    searchKey: `energy_bands.${item.key}`,
                    type: item.type,
                    hasWildCardSearch: true,
                    hasCommonSearch: false,
                    getListTable: "",
                    isWaterEnergy: true,
                    commonSearchKey: "energy_band",
                    commonSearchObjectKey: item.key,
                    pinned: tempConfig[key]?.pinned,
                    style: tempConfig[key]?.style
                };
            });
        } else {
            tempKeys = tempKeys.filter(k => k !== "energy_band_show" && !k.startsWith("energy_band_"));
            Object.keys(tempConfig).forEach(k => {
                if (k === "energy_band_show" || k.startsWith("energy_band_")) delete tempConfig[k];
            });
        }

        // 7) water band — now gated by showWaterBand
        if (showWaterBand && waterFields.length) {
            const idx = tempKeys.length > 1 ? tempKeys.length - 2 : 0;
            if (!tempKeys.includes("water_band_show")) tempKeys.splice(idx, 0, "water_band_show");

            waterFields.forEach((item, i) => {
                const key = `water_band_${i}`;
                if (!tempKeys.includes(key)) tempKeys.splice(idx, 0, key);
                tempConfig[key] = {
                    ...(tempConfig[key] || {}),
                    isVisible: tempConfig[key]?.isVisible ?? true,
                    label: item.tableLabel || item.label,
                    class: "width-230px",
                    searchKey: `water_bands.${item.key}`,
                    type: item.type,
                    hasWildCardSearch: true,
                    hasCommonSearch: false,
                    getListTable: "",
                    isWaterEnergy: true,
                    commonSearchKey: "water_band",
                    commonSearchObjectKey: item.key,
                    pinned: tempConfig[key]?.pinned,
                    style: tempConfig[key]?.style
                };
            });
        } else {
            tempKeys = tempKeys.filter(k => k !== "water_band_show" && !k.startsWith("water_band_"));
            Object.keys(tempConfig).forEach(k => {
                if (k === "water_band_show" || k.startsWith("water_band_")) delete tempConfig[k];
            });
        }
    }
    // 8) dynamic fields injection
    if (Object.keys(recommendationCustomFields).length) {
        const validDyn = new Set();
        Object.entries(recommendationCustomFields).forEach(([band, fields]) => {
            fields.forEach(field => {
                const vt = field.rules?.visible_type ?? "both";
                if (vt !== "both" && !(dynamicCR ? vt === "cr_only" : vt === "recommendation_only")) return;
                const key = field.field_name;
                if (!key) return;

                const idx = tempKeys.length > 1 ? tempKeys.length - 2 : 0;
                if (!tempKeys.includes(key)) tempKeys.splice(idx, 0, key);

                tempConfig[key] = {
                    ...(tempConfig[key] || {}),
                    isVisible: tempConfig[key]?.isVisible ?? field.rules?.table_default_visible ?? false,
                    label: field.label,
                    class: "width-230px",
                    searchKey: `dynamic_fields.${key}`,
                    type: field.field_type,
                    hasWildCardSearch: true,
                    hasCommonSearch: ["select", "boolean"].includes(field.field_type),
                    getListTable: field.field_type === "boolean" ? ["Yes", "No"] : field?.field_options.map(item => item.value) || [],
                    commonSearchKey: "dynamic_fields",
                    commonSearchObjectKey: key,
                    isDynamicField: true,
                    rules: field.rules,
                    pinned: tempConfig[key]?.pinned,
                    style: tempConfig[key]?.style ?? field.style,
                    hasLineEdit: field.field_mode !== "calculated" && !field.rules?.read_only,
                    isDropdownLineEdit: ["select", "boolean"].includes(field.field_type),
                    isMemoField: field.field_type === "memo",
                    band: field.group_field || "",
                    options: field.field_type === "boolean" ? ["Yes", "No"].map(e => ({ label: e, value: e })) : field.field_options || []
                };

                validDyn.add(key);
            });
        });

        Object.keys(tempConfig).forEach(k => {
            if (tempConfig[k]?.isDynamicField && !validDyn.has(k)) delete tempConfig[k];
        });
        tempKeys = tempKeys.filter(k => k in tempConfig);
    } else {
        tempKeys = tempKeys.filter(k => !tempConfig[k]?.isDynamicField);
        Object.keys(tempConfig).forEach(k => {
            if (tempConfig[k]?.isDynamicField) delete tempConfig[k];
        });
    }

    return {
        config: tempConfig,
        keys: _.uniq(tempKeys),
        paneViewEnabled
    };
}
